/**
 * Raw GitHub field from API rows: singular/plural, camelCase or snake_case.
 * `tasks/by-user` returns `githubLinks` (array); older paths may use `githubLink`.
 */
export function rawGithubLinkFromRow(row: unknown): unknown {
  if (row == null || typeof row !== "object") return undefined;
  const r = row as Record<string, unknown>;
  const plural = r.githubLinks ?? r.github_links ?? r.GithubLinks;
  const singular = r.githubLink ?? r.github_link ?? r.GithubLink;

  if (Array.isArray(plural) && plural.length > 0) return plural;
  if (typeof plural === "string" && plural.trim() !== "") return plural;

  return singular;
}

/**
 * Parse API GitHub payload: plain URL, JSON array string, string[], or arrays
 * whose elements are JSON-encoded arrays (e.g. one string containing two URLs).
 */
export function parseGithubLinksFromRaw(raw: unknown): string[] {
  if (raw == null) return [];

  if (Array.isArray(raw)) {
    const out: string[] = [];
    for (const x of raw) {
      if (x == null) continue;
      if (typeof x === "string") {
        const t = x.trim();
        if (!t) continue;
        if (t.startsWith("[")) {
          try {
            const p = JSON.parse(t) as unknown;
            if (Array.isArray(p)) {
              for (const y of p) {
                const u = String(y).trim();
                if (u) out.push(u);
              }
              continue;
            }
          } catch {
            /* treat as literal URL */
          }
        }
        out.push(t);
      } else if (Array.isArray(x)) {
        out.push(...parseGithubLinksFromRaw(x));
      } else {
        out.push(...parseGithubLinksFromRaw(String(x)));
      }
    }
    return out;
  }

  const s = String(raw).trim();
  if (!s) return [];
  if (s.startsWith("[")) {
    try {
      const p = JSON.parse(s) as unknown;
      if (Array.isArray(p)) {
        return parseGithubLinksFromRaw(p);
      }
    } catch {
      /* fall through */
    }
  }
  return [s];
}

export function githubLinksFromRow(row: unknown): string[] {
  return parseGithubLinksFromRaw(rawGithubLinkFromRow(row));
}

export function toGithubHref(link: string): string {
  return /^https?:\/\//i.test(link) ? link : `https://${link}`;
}

export function firstGithubLinkHref(row: unknown): string | null {
  const first = githubLinksFromRow(row)[0];
  if (!first) return null;
  return toGithubHref(first);
}
